import { useState, useCallback, useEffect } from 'react';
import useMountedState from 'react-use/lib/useMountedState';
import { Spinner } from '@shipengine/giger';
import { useSdk } from '@packlink/packlink-sdk-provider';
import { useTranslation } from '@packlink/translation-provider';
import { PaymentMethod } from '@packlink/packlink-sdk';
import { PaymentOption } from '../PaymentOption/PaymentOption.js';
import { PaymentOptionLocal } from '../PaymentOptionLocal/PaymentOptionLocal.js';
import { PaymentOptionPaypal } from '../PaymentOptionPaypal/PaymentOptionPaypal.js';
import { PaymentOptionCreditCard } from '../PaymentOptionCreditCard/PaymentOptionCreditCard.js';
import { getSectionLoadingStyles } from './PaymentOptionsStyles.js';
import { jsxs, jsx, Fragment } from '@emotion/react/jsx-runtime';
import { LoggerPaymentBreadcrumb, LoggerPaymentContext, LoggerPaymentTag, PaymentStep, OptionImgSize, SupportedCardImages, LoggerPaymentErrorTagValues } from '../../../types/types.js';
import { initClient } from '../../../utils/braintree.js';
import { addBreadcrumb, captureException, logApliClientError } from '../../../utils/Logger.js';

var BREADCRUMBS = /*#__PURE__*/function (BREADCRUMBS) {
  BREADCRUMBS["SETUP_ERROR"] = "Client setup error";
  BREADCRUMBS["SETUP_SUCCESS"] = "Client setup success";
  return BREADCRUMBS;
}(BREADCRUMBS || {});
const PaymentOptions = _ref => {
  let {
    allowedPaymentMethods = [PaymentMethod.CREDITCARD3DSECURE, PaymentMethod.PAYPAL, PaymentMethod.BANCONTACT],
    amount,
    cdnUrl,
    client,
    creditCard,
    currency,
    fallbackOptions,
    isPaymentBlocked = false,
    isProcessing,
    locale,
    paypal,
    selectedPaymentMethod,
    sendBillingAddress = false,
    payButtonTexts,
    onCheckForExternalError,
    onError,
    onReady,
    onStart,
    onVerified,
    onRequestClientNonce,
    onDeletePaymentMethod,
    onChangeOption,
    ...rest
  } = _ref;
  const sdk = useSdk();
  const {
    t
  } = useTranslation();
  const isMounted = useMountedState();
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [clientToken, setClientToken] = useState();
  const [clientBraintreeInstance, setClientBraintreeInstance] = useState();
  const [selectedOption, setSelectedOption] = useState(allowedPaymentMethods.length === 1 ? allowedPaymentMethods[0] : selectedPaymentMethod);
  const addBreadcrumbMsg = useCallback(msg => addBreadcrumb(LoggerPaymentBreadcrumb.PAYMENT, msg), []);
  const onHasTokenizationParams = useCallback(message => {
    addBreadcrumbMsg(message);
  }, [addBreadcrumbMsg]);
  const onSetupSuccess = useCallback(message => {
    addBreadcrumbMsg(message);
  }, [addBreadcrumbMsg]);
  const onSetupError = useCallback((error, msg, tag, paymentMethod) => {
    addBreadcrumbMsg(msg);
    captureException(error || new Error(tag), {
      contexts: {
        [LoggerPaymentContext.PAYMENT_SETUP]: {
          ...error
        }
      },
      tags: {
        [LoggerPaymentTag.PAYMENT_ERROR]: tag
      }
    });
    onError === null || onError === void 0 ? void 0 : onError(PaymentStep.SETUP, paymentMethod, error);
  }, [onError, addBreadcrumbMsg]);
  const onChange = useCallback(e => {
    setSelectedOption(e.target.value);
    onChangeOption === null || onChangeOption === void 0 ? void 0 : onChangeOption(e.target.value);
  }, [onChangeOption]);
  const onVerificationStarts = useCallback(type => msg => {
    addBreadcrumbMsg(msg);
    onStart === null || onStart === void 0 ? void 0 : onStart(type);
  }, [onStart, addBreadcrumbMsg]);
  const onVerificationSuccess = useCallback((paymentMethod, msg, verification, paymentMetrics) => {
    addBreadcrumbMsg(msg);
    onVerified(paymentMethod, verification, paymentMetrics);
  }, [onVerified, addBreadcrumbMsg]);
  const onVerificationError = useCallback((paymentMethod, msg, tag, error) => {
    addBreadcrumbMsg(msg);
    onError === null || onError === void 0 ? void 0 : onError(PaymentStep.VERIFICATION, paymentMethod, error);
    captureException(error || new Error(tag), {
      contexts: {
        [LoggerPaymentContext.PAYMENT_VERIFICATION_ERROR]: {
          ...error
        }
      },
      tags: {
        [LoggerPaymentTag.PAYMENT_ERROR]: tag
      }
    });
  }, [onError, addBreadcrumbMsg]);
  const onDelete = useCallback(payment => {
    sdk.v1.billing.delete(payment).then(() => onDeletePaymentMethod === null || onDeletePaymentMethod === void 0 ? void 0 : onDeletePaymentMethod(payment.id, payment.method)).catch(logApliClientError);
  }, [sdk, onDeletePaymentMethod]);
  const getCardImg = () => {
    const imageName = (creditCard === null || creditCard === void 0 ? void 0 : creditCard.cardType) && SupportedCardImages[creditCard === null || creditCard === void 0 ? void 0 : creditCard.cardType.toUpperCase()] || 'generic';
    return `${cdnUrl}/apps/payment-cards/${imageName}.svg`;
  };
  useEffect(() => {
    if (!onReady) {
      return;
    }
    if (!isLoading && isReady && !!clientBraintreeInstance) {
      onReady === null || onReady === void 0 ? void 0 : onReady();
    }
  }, [clientBraintreeInstance, isLoading, isReady, onReady]);
  useEffect(() => {
    setIsLoading(true);
    const onRequestClientNoncePromise = onRequestClientNonce(currency);
    onRequestClientNoncePromise.then(setClientToken).catch(logApliClientError).finally(() => {
      setIsLoading(false);
    });
    return () => {
      if (onRequestClientNoncePromise.isPending()) {
        onRequestClientNoncePromise.cancel();
      }
    };
  }, [currency, onRequestClientNonce]);
  useEffect(() => {
    const initializeBraintree = async nonce => {
      try {
        const clientBraintree = await initClient(nonce);
        if (isMounted()) {
          setClientBraintreeInstance(clientBraintree);
          onSetupSuccess(BREADCRUMBS.SETUP_SUCCESS);
          setIsReady(true);
        }
      } catch (error) {
        if (error instanceof Error && isMounted()) {
          onSetupError(error, BREADCRUMBS.SETUP_ERROR, LoggerPaymentErrorTagValues.PAYMENT_CLIENT_SETUP);
          setIsReady(false);
        }
      }
    };
    if (clientToken !== null && clientToken !== void 0 && clientToken.nonce && !clientBraintreeInstance) {
      initializeBraintree(clientToken.nonce);
    }
  }, [clientBraintreeInstance, clientToken, isMounted, onSetupError, onSetupSuccess]);
  return jsxs("section", {
    css: (isLoading || !isReady) && getSectionLoadingStyles,
    ...rest,
    children: [(isLoading || !isReady) && jsx(Spinner, {}), !isLoading && isReady && clientBraintreeInstance && jsxs(Fragment, {
      children: [allowedPaymentMethods.includes(PaymentMethod.CREDITCARD3DSECURE) && jsx(PaymentOption, {
        label: t('payment-options.label.credit-card'),
        value: PaymentMethod.CREDITCARD3DSECURE,
        selectedValue: selectedOption,
        image: creditCard && getCardImg(),
        size: OptionImgSize.LARGE,
        onChange: onChange,
        children: jsx(PaymentOptionCreditCard, {
          amount: amount,
          creditCard: creditCard,
          client: client,
          clientBraintree: clientBraintreeInstance,
          isPaymentBlocked: isPaymentBlocked,
          isProcessing: isProcessing,
          sendBillingAddress: sendBillingAddress,
          cdnUrl: cdnUrl,
          payButtonText: payButtonTexts === null || payButtonTexts === void 0 ? void 0 : payButtonTexts.creditCard,
          onCheckForExternalError: onCheckForExternalError,
          onError: onError,
          onDelete: onDelete,
          onSetupError: onSetupError,
          onSetupSuccess: onSetupSuccess,
          onVerificationError: onVerificationError,
          onVerificationSuccess: onVerificationSuccess,
          onVerificationStart: onVerificationStarts(PaymentMethod.CREDITCARD3DSECURE)
        })
      }), allowedPaymentMethods.includes(PaymentMethod.PAYPAL) && jsx(PaymentOption, {
        label: t('payment-options.label.paypal'),
        value: PaymentMethod.PAYPAL,
        selectedValue: selectedOption,
        image: `${cdnUrl}/apps/giger/partners-logos/paypal.svg`,
        onChange: onChange,
        children: jsx(PaymentOptionPaypal, {
          amount: amount,
          paypal: paypal,
          clientBraintree: clientBraintreeInstance,
          currency: currency,
          locale: locale,
          cdnUrl: cdnUrl,
          isPaymentBlocked: isPaymentBlocked,
          isProcessing: isProcessing,
          payButtonText: payButtonTexts === null || payButtonTexts === void 0 ? void 0 : payButtonTexts.paypal,
          onCheckForExternalError: onCheckForExternalError,
          onDelete: onDelete,
          onSetupError: onSetupError,
          onSetupSuccess: onSetupSuccess,
          onVerificationCancel: addBreadcrumbMsg,
          onVerificationError: onVerificationError,
          onVerificationSuccess: onVerificationSuccess,
          onVerificationStart: onVerificationStarts(PaymentMethod.PAYPAL)
        })
      }), allowedPaymentMethods.includes(PaymentMethod.BANCONTACT) && jsx(PaymentOption, {
        label: "Bancontact",
        value: PaymentMethod.BANCONTACT,
        selectedValue: selectedOption,
        image: `${cdnUrl}/apps/payment-method-logos/bancontact.svg`,
        onChange: onChange,
        children: jsx(PaymentOptionLocal, {
          cdnUrl: cdnUrl,
          clientBraintree: clientBraintreeInstance,
          isPaymentBlocked: isPaymentBlocked,
          isProcessing: isProcessing,
          amount: amount,
          currency: currency,
          type: PaymentMethod.BANCONTACT,
          fallbackOptions: fallbackOptions,
          onCheckForExternalError: onCheckForExternalError,
          onSetupSuccess: onSetupSuccess,
          onSetupError: onSetupError,
          onVerificationError: onVerificationError,
          onVerificationSuccess: onVerificationSuccess,
          onHasTokenizationParams: onHasTokenizationParams,
          onVerificationStart: onVerificationStarts(PaymentMethod.BANCONTACT)
        })
      })]
    })]
  });
};

export { PaymentOptions };
//# sourceMappingURL=PaymentOptions.js.map
